import { useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Loading, Page, TableScroller, Tabs, useAsync } from '../../components/common';
import { formatBytes } from '../../lib/format';
import { useParam } from '../../pages/statistics/shared';
import { LineChart, type LineSeries } from '@nyce/admin-kit/console';
import { automationGw, type RouteStats } from '../api/client';
import { useAutomationTarget } from '../shared';
import '../../pages/statistics/statistics.css';
import '../automation.css';

const RANGES = [
  { value: '1', label: '1 h', stepMs: 60_000 },
  { value: '6', label: '6 h', stepMs: 5 * 60_000 },
  { value: '24', label: '24 h', stepMs: 15 * 60_000 },
  { value: '168', label: '7 d', stepMs: 3_600_000 },
];
const TOP = 6;

export interface RouteTraffic {
  routeId: string;
  label: string;
  toAgent: number;
  fromAgent: number;
  /** Per bucket: bytes moved in it, and the most channels open at once. */
  buckets: { t: number; toAgent: number; fromAgent: number; channels: number }[];
}

/**
 * The gateway samples each route's counters every minute; they count up since its pod started, so traffic per bucket is
 * the sum of the increases (a drop = the pod restarted, the new value counts whole).
 */
export function routeTraffic(r: RouteStats, stepMs: number): RouteTraffic {
  const pts = r.dataPoints
    .map((p) => ({ ...p, t: new Date(p.timestamp).getTime() }))
    .filter((p) => Number.isFinite(p.t))
    .sort((a, b) => a.t - b.t);
  const buckets = new Map<number, { t: number; toAgent: number; fromAgent: number; channels: number }>();
  let toAgent = 0;
  let fromAgent = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const prev = pts[i - 1];
    const dTo = !prev
      ? 0
      : p.bytesToAgent >= prev.bytesToAgent
        ? p.bytesToAgent - prev.bytesToAgent
        : p.bytesToAgent;
    const dFrom = !prev
      ? 0
      : p.bytesFromAgent >= prev.bytesFromAgent
        ? p.bytesFromAgent - prev.bytesFromAgent
        : p.bytesFromAgent;
    const k = Math.floor(p.t / stepMs) * stepMs;
    const b = buckets.get(k) ?? { t: k, toAgent: 0, fromAgent: 0, channels: 0 };
    b.toAgent += dTo;
    b.fromAgent += dFrom;
    b.channels = Math.max(b.channels, p.activeChannels);
    buckets.set(k, b);
    toAgent += dTo;
    fromAgent += dFrom;
  }
  return {
    routeId: r.routeId,
    label: `${r.clientName} :${r.publicPort}`,
    toAgent,
    fromAgent,
    buckets: [...buckets.values()],
  };
}

/** Bytes and channels per route over time, busiest routes on the charts, every route in the table. */
export function TrafficPage() {
  const target = useAutomationTarget();
  const api = useMemo(() => automationGw(target), [target]);
  const [hours, setHours] = useParam('hours', '24');
  const range = RANGES.find((r) => r.value === hours) ?? RANGES[2];
  const stats = useAsync(() => api.statistics(Number(range.value)), [api, range.value]);
  const to = Date.now();
  const from = to - Number(range.value) * 3_600_000;
  const routes = (stats.data?.routes ?? [])
    .map((r) => routeTraffic(r, range.stepMs))
    .sort((a, b) => b.toAgent + b.fromAgent - (a.toAgent + a.fromAgent));
  const top = routes.slice(0, TOP);
  const series = (pick: (b: RouteTraffic['buckets'][number]) => number): LineSeries[] =>
    top.map((r) => ({
      key: r.routeId,
      label: r.label,
      points: r.buckets.map((b) => ({ t: b.t, v: pick(b) })),
    }));
  const step = range.stepMs >= 3_600_000 ? `${range.stepMs / 3_600_000} h` : `${range.stepMs / 60_000} min`;

  return (
    <Page
      title="Traffic"
      description="Bytes through each route and the channels open on it, from the gateway's minute samples (kept 7 days)."
      actions={
        <Button
          size="sm"
          variant="ghost"
          icon={<RefreshCw size={14} />}
          loading={stats.loading}
          onClick={() => void stats.reload()}
        >
          Refresh
        </Button>
      }
    >
      <div className="ag-toolbar">
        <Tabs value={range.value} onChange={setHours} options={RANGES} />
      </div>
      <ErrorBox error={stats.error} />
      <Loading loading={stats.loading && !stats.data} />
      {stats.data && !routes.length && <EmptyState card title="No route statistics in this range." />}
      {!!routes.length && (
        <div className={stats.loading ? 'oph-dash-stale' : undefined} data-testid="ag-traffic">
          <div className="bd-grid">
            <LineChart
              title={`To agents per ${step}`}
              series={series((b) => b.toAgent)}
              from={from}
              to={to}
              stepMs={range.stepMs}
              format={formatBytes}
            />
            <LineChart
              title={`From agents per ${step}`}
              series={series((b) => b.fromAgent)}
              from={from}
              to={to}
              stepMs={range.stepMs}
              format={formatBytes}
            />
            <LineChart
              title="Open channels (peak)"
              series={series((b) => b.channels)}
              from={from}
              to={to}
              stepMs={range.stepMs}
              format={(v) => String(Math.round(v))}
            />
          </div>
          {routes.length > TOP && <p className="nyk-ui-hint">The charts show the {TOP} busiest routes.</p>}
          <div className="nyk-card">
            <TableScroller>
              <table className="nyk-ui-table nyk-cards" data-testid="ag-traffic-routes">
                <thead>
                  <tr>
                    <th>Route</th>
                    <th className="nyk-ui-num">To agent</th>
                    <th className="nyk-ui-num">From agent</th>
                    <th className="nyk-ui-num">Peak channels</th>
                  </tr>
                </thead>
                <tbody>
                  {routes.map((r) => (
                    <tr key={r.routeId} data-route={r.routeId}>
                      <td className="nyk-card-title">{r.label}</td>
                      <td className="nyk-ui-num" data-label="To agent">
                        {formatBytes(r.toAgent)}
                      </td>
                      <td className="nyk-ui-num" data-label="From agent">
                        {formatBytes(r.fromAgent)}
                      </td>
                      <td className="nyk-ui-num" data-label="Peak channels">
                        {Math.max(0, ...r.buckets.map((b) => b.channels))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
          </div>
        </div>
      )}
    </Page>
  );
}
